import { useState } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Alert, App, Button, Drawer, Flex, Popconfirm, Table, Typography } from 'antd';
import PropTypes from 'prop-types';
import {
  deleteExpenseExclusion,
  listExpenseExclusions,
  previewExpenseExclusion,
  saveExpenseExclusion,
} from '../services/expense-exclusion';
import { describeExpenseExclusion } from '../utils/expense-exclusion';
import ExpenseExclusionEditor from './ExpenseExclusionEditor';
import ExpenseExclusionPreview from './ExpenseExclusionPreview';

const ExpenseExclusionDrawer = ({ organizationId, onClose }) => {
  const [editing, setEditing] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const { message } = App.useApp();
  const client = useQueryClient();
  const key = ['expense-exclusion-rules', organizationId];
  const rules = useQuery({ queryKey: key, queryFn: () => listExpenseExclusions(organizationId) });
  const refresh = () => {
    setEditing(null);
    setPreview(null);
    client.invalidateQueries({ queryKey: key });
  };
  const action = useMutation({
    mutationFn: async ({ type, rule }) => {
      setError(null);
      if (type === 'preview') {
        setPreview(await previewExpenseExclusion(organizationId));
        return;
      }
      if (type === 'delete') await deleteExpenseExclusion(organizationId, rule.id);
      else await saveExpenseExclusion(organizationId, rule.id, { ...rule, enabled: false });
      refresh();
      message.success(type === 'delete' ? '规则已删除' : '规则已停用');
    },
    onError: (err) => {
      setPreview(null);
      setError(err.message || '操作失败，请重试');
    },
  });
  return (
    <Drawer open title="费用排除规则" size={1000} onClose={onClose} destroyOnHidden>
      <Flex vertical gap="middle">
        <Alert
          type="info"
          showIcon
          message="仅影响经营费用参与指标计算的资格；原始事件、来源日志和映射保持不变。无启用规则时，计算保持原样。"
        />
        <Flex gap="small">
          <Button type="primary" onClick={() => setEditing({ enabled: true })}>
            新增规则
          </Button>
          <Button loading={action.isPending} onClick={() => action.mutate({ type: 'preview' })}>
            预览全部启用规则
          </Button>
        </Flex>
        {rules.isError && (
          <Alert
            type="error"
            showIcon
            message="规则加载失败"
            action={<Button onClick={() => rules.refetch()}>重试</Button>}
          />
        )}
        {error && <Alert type="error" showIcon message={error} />}
        <Table
          rowKey="id"
          loading={rules.isPending}
          dataSource={rules.data || []}
          columns={[
            { title: '排除条件', render: (_, rule) => describeExpenseExclusion(rule) },
            {
              title: '状态',
              dataIndex: 'enabled',
              render: (enabled) => (enabled ? '启用' : '停用'),
            },
            {
              title: '操作',
              render: (_, rule) => (
                <Flex gap="small" wrap>
                  <Button type="link" onClick={() => setEditing(rule)}>
                    编辑
                  </Button>
                  {rule.enabled ? (
                    <Popconfirm
                      title="停用后，该规则不再排除费用，是否继续？"
                      onConfirm={() => action.mutate({ type: 'disable', rule })}
                    >
                      <Button type="link" disabled={action.isPending}>
                        停用
                      </Button>
                    </Popconfirm>
                  ) : (
                    <Button type="link" onClick={() => setEditing({ ...rule, enabled: true })}>
                      启用
                    </Button>
                  )}
                  <Popconfirm
                    title="删除此费用排除规则？"
                    onConfirm={() => action.mutate({ type: 'delete', rule })}
                  >
                    <Button type="link" danger disabled={action.isPending}>
                      删除
                    </Button>
                  </Popconfirm>
                </Flex>
              ),
            },
          ]}
        />
        {preview && (
          <>
            <Typography.Title level={5}>全部启用规则合计（事件去重）</Typography.Title>
            <ExpenseExclusionPreview preview={preview} />
          </>
        )}
      </Flex>
      {editing && (
        <ExpenseExclusionEditor
          organizationId={organizationId}
          rule={editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      )}
    </Drawer>
  );
};
ExpenseExclusionDrawer.propTypes = {
  organizationId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};
export default ExpenseExclusionDrawer;
